(() => {
  const loader = document.getElementById('OdeHomeLoader');

  if (!loader) return;

  try {
    if (window.sessionStorage.getItem('ode-home-loader-seen')) {
      loader.remove();
      return;
    }

    window.sessionStorage.setItem('ode-home-loader-seen', 'true');
  } catch (error) {
    // Keep the loader available when session storage is unavailable.
  }

  const numbers = loader.querySelector('[data-home-loader-numbers]');
  const numbersWrap = loader.querySelector('.ode-home-loader__numbers-wrap');
  const loaderContent = loader.querySelector('.ode-home-loader__content');
  const loaderMessage = loader.querySelector('.ode-home-loader__message');
  const status = loader.querySelector('[data-home-loader-status]');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const startedAt = performance.now();
  const rapidDuration = 1850;
  const completionHoldDuration = 500;
  const loaderExitDuration = 900;
  const finalRollDuration = 2500;
  const numberElements = Array.from(numbers.children);
  const itemWidth = numberElements[0]?.getBoundingClientRect().width || 26;
  const rollerEdgeInset = numbersWrap.clientWidth * 0.07;
  const finalPosition =
    (numbers.scrollWidth - numbersWrap.clientWidth + rollerEdgeInset) / itemWidth;
  const slowdownStartPosition = finalPosition - 7;
  let assetsReady = false;
  let finalRollStartedAt = null;
  let exitStartedAt = null;
  let entranceStarted = false;
  let finished = false;
  let activeIndex = -1;
  let announcedProgress = 0;

  document.documentElement.classList.add('ode-home-is-loading');

  const clamp = (value) => Math.max(0, Math.min(1, value));
  const range = (time, start, end) => clamp((time - start) / (end - start));
  const lerp = (start, end, progress) => start + (end - start) * progress;
  const easeOutQuint = (progress) => 1 - Math.pow(1 - progress, 5);
  const easeOutCubic = (progress) => 1 - Math.pow(1 - progress, 3);
  const easeOutQuintWithMomentum = (progress) =>
    easeOutQuint(progress) * 0.8 + progress * 0.2;
  const easeInOutCubic = (progress) =>
    progress < 0.5
      ? 4 * progress * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 3) / 2;

  const setActiveNumber = (index) => {
    if (index === activeIndex) return;

    activeIndex = index;
    numberElements.forEach((number, numberIndex) => {
      number.toggleAttribute('data-active', numberIndex === index);
    });

    const progress = index + 1;
    const nextAnnouncement = progress === 100 ? 100 : Math.floor(progress / 10) * 10;

    if (nextAnnouncement > announcedProgress) {
      announcedProgress = nextAnnouncement;
      status.textContent = `Loading home page: ${nextAnnouncement}%`;
    }
  };

  const beginEntrance = () => {
    if (entranceStarted) return;

    entranceStarted = true;
    document.documentElement.classList.replace('ode-home-is-loading', 'ode-home-is-entering');
  };

  const render = (now) => {
    const elapsed = now - startedAt;
    let position;

    if (elapsed < rapidDuration) {
      const progress = easeOutQuintWithMomentum(range(elapsed, 0, rapidDuration));
      position = lerp(0, slowdownStartPosition, progress);
    } else if (!assetsReady) {
      position = slowdownStartPosition;
    } else {
      if (finalRollStartedAt === null) finalRollStartedAt = now;

      const progress = easeOutCubic(
        range(now, finalRollStartedAt, finalRollStartedAt + finalRollDuration),
      );
      position = lerp(slowdownStartPosition, finalPosition, progress);

      const holdComplete =
        now >= finalRollStartedAt + finalRollDuration + completionHoldDuration;

      if (progress === 1 && exitStartedAt === null && holdComplete) {
        exitStartedAt = now;
        beginEntrance();
      }
    }

    numbers.style.transform = `translate3d(${-position * itemWidth}px, 0, 0)`;
    loaderMessage.style.transform = `translate3d(${lerp(0, -2, easeOutCubic(range(position, 0, finalPosition)))}px, 0, 0)`;
    const activeNumber =
      position >= finalPosition - 0.25
        ? numberElements.length - 1
        : Math.min(numberElements.length - 1, Math.floor(position) + 4);
    setActiveNumber(activeNumber);

    if (exitStartedAt !== null) {
      const exit = easeInOutCubic(range(now, exitStartedAt, exitStartedAt + loaderExitDuration));

      loaderContent.style.transform = `translate3d(${lerp(0, -22, exit)}px, 0, 0)`;
      loader.style.opacity = String(1 - exit);
      loader.style.pointerEvents = exit > 0.9 ? 'none' : 'auto';

      if (exit === 1) {
        finished = true;
        loader.remove();

        document.documentElement.classList.remove('ode-home-is-entering');
      }
    }
  };

  const tick = (now) => {
    render(now);
    if (!finished) window.requestAnimationFrame(tick);
  };

  const preload = (image) => {
    const source = image.currentSrc || image.src;

    if (!source || source.startsWith('data:')) return Promise.resolve();

    const preloadImage = new Image();
    preloadImage.src = source;

    if (typeof preloadImage.decode === 'function') {
      return preloadImage.decode().catch(() => undefined);
    }

    return new Promise((resolve) => {
      preloadImage.onload = preloadImage.onerror = resolve;
    });
  };

  const preloadHomeImages = async () => {
    const images = Array.from(document.querySelectorAll('#MainContent img'));

    if (!images.length) return;

    await Promise.all(images.map(preload));
  };

  if (reduceMotion) {
    document.documentElement.classList.remove('ode-home-is-loading');
    loader.remove();
    return;
  }

  render(startedAt);
  window.requestAnimationFrame(tick);

  Promise.all([
    preloadHomeImages(),
    new Promise((resolve) => {
      if (document.readyState === 'complete') {
        resolve();
      } else {
        window.addEventListener('load', resolve, { once: true });
      }
    }),
  ]).then(() => {
    assetsReady = true;
  });

})();
